import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useIonViewWillEnter } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import { detectBrowserLocation } from '../../../infrastructure/browser/geolocation';
import type { Device } from '../../../domain/Device';
import { selectionToSpecs, type ComponentSelection } from '../../../domain/Component';
import type { Site } from '../../../domain/Site';
import type { StationNow } from '../../../domain/Lot';
import { joinPathForScan } from '../../../domain/DeviceShare';
import { hubClaimPathForScan, type Hub } from '../../../domain/Hub';

// La estación DOMIOT trae de fábrica clima (bme280, que mide también la
// presión), pluviómetro y viento.
const DEFAULT_SELECTION: ComponentSelection = {
  air: true,
  pressure: true,
  rain: true,
  wind: true,
  soil: 0,
  valve: 0,
};

export function useDevicesPage() {
  const history = useHistory();
  const { devices: deviceRepo, sites: siteRepo, lots: lotRepo, hubs: hubRepo } = useRepositories();

  const [devices, setDevices] = useState<Device[]>([]);
  // Los hubs del usuario: la "conexión" de cada campo.
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  // Lo último de cada estación y los lotes (para el filtro y el nombre del lote).
  const [now, setNow] = useState<Record<string, StationNow>>({});
  const [lots, setLots] = useState<{ id: string; name: string }[]>([]);
  // null = todas.
  const [lotFilter, setLotFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // El spinner es solo para la primera carga: al volver a la pantalla (o en
  // cada refresco) los datos se actualizan sin mover la página.
  const loadedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const [showScanner, setShowScanner] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newComponents, setNewComponents] = useState<ComponentSelection>(DEFAULT_SELECTION);
  const [newSiteId, setNewSiteId] = useState<string | null>(null);
  const [newLat, setNewLat] = useState<number | null>(null);
  const [newLng, setNewLng] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);

  const load = async () => {
    if (!loadedRef.current) setLoading(true);
    setError(null);
    try {
      const [{ devices: list }, { sites: siteList }, nowRes, lotsRes, hubsRes] = await Promise.all([
        deviceRepo.list(),
        siteRepo.list(),
        // Los valores y los lotes son un extra: si fallan, la lista igual se ve.
        deviceRepo.listNow().catch(() => ({ now: {} })),
        lotRepo.list().catch(() => ({ lots: [] })),
        hubRepo.list().catch(() => ({ hubs: [] })),
      ]);
      setDevices(list);
      setSites(siteList);
      setNow(nowRes.now);
      setLots(lotsRes.lots.map((l) => ({ id: l.id, name: l.name })));
      setHubs(hubsRes.hubs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar las estaciones.');
    } finally {
      loadedRef.current = true;
      setLoading(false);
    }
  };

  useIonViewWillEnter(() => {
    load();
  });

  // El mismo lector sirve para la etiqueta de un hub y para las invitaciones.
  const handleScan = (decodedText: string) => {
    setShowScanner(false);
    history.push(hubClaimPathForScan(decodedText) ?? joinPathForScan(decodedText));
  };

  const openAdd = () => {
    setNewName('');
    setNewComponents(DEFAULT_SELECTION);
    setNewSiteId(null);
    setNewLat(null);
    setNewLng(null);
    setFormError(null);
    setShowAdd(true);
  };

  // Al dar de alta el equipo en el campo, el celular está al lado.
  const handleUseMyLocation = async () => {
    setLocating(true);
    setFormError(null);
    try {
      const { lat, lon } = await detectBrowserLocation();
      setNewLocation(lat, lon);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'No se pudo obtener tu ubicación.');
    } finally {
      setLocating(false);
    }
  };

  const setNewLocation = (lat: number, lng: number) => {
    setNewLat(lat);
    setNewLng(lng);
  };

  const handleToggleFavorite = async (device: Device) => {
    const nextFavorite = !device.isFavorite;
    setDevices((prev) => prev.map((d) => (d.id === device.id ? { ...d, isFavorite: nextFavorite } : d)));
    try {
      await deviceRepo.setFavorite(device.id, nextFavorite);
    } catch {
      setDevices((prev) => prev.map((d) => (d.id === device.id ? { ...d, isFavorite: device.isFavorite } : d)));
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      setFormError('Ingresá un nombre para la estación.');
      return;
    }
    setCreating(true);
    setFormError(null);
    try {
      await deviceRepo.create({
        name: newName.trim(),
        components: selectionToSpecs(newComponents),
        siteId: newSiteId,
        lat: newLat,
        lng: newLng,
      });
      setShowAdd(false);
      load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'No se pudo crear la estación.');
    } finally {
      setCreating(false);
    }
  };

  return {
    devices,
    visibleDevices: lotFilter ? devices.filter((d) => d.lotId === lotFilter) : devices,
    now,
    lots: lots.filter((l) => devices.some((d) => d.lotId === l.id)),
    lotFilter,
    setLotFilter,
    sites,
    loading,
    error,
    showScanner,
    setShowScanner,
    showAdd,
    setShowAdd,
    newName,
    setNewName,
    newComponents,
    setNewComponents,
    newSiteId,
    setNewSiteId,
    newLat,
    newLng,
    setNewLocation,
    handleUseMyLocation,
    locating,
    creating,
    formError,
    handleScan,
    hubs,
    showAddMenu,
    setShowAddMenu,
    goToHub: (id: string) => history.push(`/app/hubs/${id}`),
    goToClaimHub: () => history.push('/app/hubs/vincular'),
    goToAddStation: () => history.push('/app/estaciones/nueva'),
    openAdd,
    handleCreate,
    handleToggleFavorite,
    // La estación con sus datos; su configuración se abre desde ahí (⚙).
    goToDevice: (id: string) => history.push(`/app/stations/${id}`),
    goToMap: () => history.push('/app/map'),
  };
}
